<section class="sign-page">
  <div class="sign-wrap" [class.with-consent-bar]="!hasUnlockedSigningUi">
    @if (loading) {
      <p class="state-banner">Loading signing request...</p>
    } @else if (errorMessage) {
      <p class="error-banner">{{ errorMessage }}</p>
    }

    @if (session && isReadOnlyView) {
      <app-read-only-pdf-review
        [eyebrow]="readOnlyReviewEyebrow"
        [title]="readOnlyReviewTitle"
        [copy]="readOnlyReviewCopy"
        [metaItems]="readOnlyReviewMetaItems"
        [pdfUrl]="pdfUrl"
        [documentId]="session.document.id"
        [fields]="session.fields"
        [fieldValues]="readOnlyFieldValues"
        [brandSignatureFrame]="session.brandSignatureFrame ?? signatureFrameSettings"
        [signatureFrameMetadata]="readOnlySignatureFrameMetadata"
        [tone]="isDeclinedView ? 'declined' : 'default'"
      ></app-read-only-pdf-review>
    } @else if (session) {
      <header class="sign-topbar">
        <div class="brand-row">
          <p class="doc-title">{{ session.document.title }}</p>
          <p class="required-left">Required fields left <span>{{ requiredLeftCount }}</span></p>
        </div>

        <div class="top-actions">
          <button type="button" class="top-btn">EN</button>
          <button type="button" class="top-btn top-btn-danger" (click)="openDeclineModal()" [disabled]="declining || submitting">
            {{ declining ? 'Declining...' : 'Decline request' }}
          </button>
          <button
            type="button"
            class="top-btn top-btn-primary"
            (click)="startOrAdvance()"
            [disabled]="!canUsePrimaryAction"
          >
            {{ submitting ? 'Finishing...' : primaryActionLabel }}
          </button>
        </div>
      </header>

      <div class="sign-layout" [class.locked]="!hasUnlockedSigningUi">
        <aside class="left-rail" #walkthroughRail>
          @if (hasUnlockedSigningUi) {
            <button
              type="button"
              class="walkthrough-callout"
              (click)="startOrAdvance()"
              [disabled]="submitting || !canUsePrimaryAction"
              [style.top.px]="walkthroughArrowOffset"
            >
              <span>{{ leftGuideLabel }}</span>
            </button>
          }
        </aside>

        <section class="viewer-stage" #viewerHost>
          <div class="viewer-wrap">
            @if (pdfUrl) {
              <ngx-extended-pdf-viewer
                [src]="pdfUrl"
                [disableForms]="true"
                [textLayer]="false"
                [showFindButton]="false"
                [showPagingButtons]="false"
                [showPreviousAndNextPageButtons]="false"
                [showFirstAndLastPageButtons]="false"
                [showPageNumber]="false"
                [showZoomButtons]="false"
                [showZoomDropdown]="false"
                [showSidebarButton]="false"
                [showSecondaryToolbarButton]="false"
                [showPresentationModeButton]="false"
                [showOpenFileButton]="false"
                [showPrintButton]="false"
                [showDownloadButton]="false"
                [showPropertiesButton]="false"
                [showRotateButton]="false"
                [showHandToolButton]="false"
                [showEditorButtons]="false"
                [showPageFlipButton]="false"
                [showSpreadButton]="false"
                [rotation]="viewerRotation"
                [zoom]="zoomLevel"
                [page]="viewerPage"
                [height]="'calc(100vh - 9.25rem)'"
                (pagesLoaded)="onPagesLoaded($event)"
                (pageChange)="onPageChange($event)"
                (pageRendered)="onPageRendered()"
                (rotationChange)="onViewerRotationChange($event)"
              ></ngx-extended-pdf-viewer>
            } @else {
              <p class="empty">Preview unavailable.</p>
            }
          </div>
        </section>
      </div>

      @if (!hasUnlockedSigningUi) {
        <section class="consent-bar">
          <label class="consent-check">
            <input type="checkbox" [(ngModel)]="hasAcceptedDisclosure" (ngModelChange)="onDisclosureAcceptanceChange()" />
            <span>
              I have read and agreed to the
              <button type="button" class="terms-link" (click)="openDisclosureModal()">
                Electronic Signature Disclosure Terms
              </button>
            </span>
          </label>

          <div class="consent-actions">
            <button
              type="button"
              class="primary consent-continue"
              (click)="continueSigning()"
              [disabled]="submitting || !hasAcceptedDisclosure"
            >
              Continue
            </button>
            <button type="button" class="locale-pill" aria-label="Language selector">
              EN
            </button>
          </div>
        </section>
      }

      @if (!hasUnlockedSigningUi) {
        <div class="content-lock-overlay" aria-hidden="true"></div>
      }

      @if (isMarkModalOpen) {
        <div class="modal-overlay" (click)="closeMarkModal()">
          <section class="mark-modal" role="dialog" aria-modal="true" aria-label="Signature and Initials" (click)="$event.stopPropagation()">
            <header class="modal-header">
              <h2>{{ markKind === 'initials' ? 'Initials' : 'Signature' }}</h2>
              <button type="button" class="modal-close" aria-label="Close mark modal" (click)="closeMarkModal()">x</button>
            </header>

            <div class="mark-tab-row">
              @for (tab of markTabs; track tab) {
                <button type="button" [class.active]="markTab === tab" (click)="selectMarkTab(tab)">
                  {{ tab === 'type' ? 'Type' : tab === 'draw' ? 'Draw' : 'Upload' }}
                </button>
              }
            </div>

            @if (markTab === 'type') {
              <div class="mark-type-pane">
                <label>
                  Your name
                  <input type="text" [(ngModel)]="markTypeInput" />
                </label>

                <div class="type-style-grid">
                  @for (style of typeStyleOptions; track style.id; let i = $index) {
                    <button type="button" class="type-style-card" [class.active]="markTypeStyleIndex === i" (click)="selectTypeStyle(i)">
                      <span [style.font-family]="style.fontFamily">{{ markTypeInput || (markKind === 'initials' ? 'IN' : 'Signer') }}</span>
                    </button>
                  }
                </div>
              </div>
            }

            @if (markTab === 'draw') {
              <div class="mark-draw-pane">
                <button type="button" class="clear-link" (click)="clearDrawCanvas()">Clear</button>
                <canvas
                  #drawCanvas
                  class="draw-canvas"
                  (pointerdown)="onDrawPointerDown($event)"
                  (pointermove)="onDrawPointerMove($event)"
                  (pointerup)="onDrawPointerUp($event)"
                  (pointerleave)="onDrawPointerUp($event)"
                ></canvas>
              </div>
            }

            @if (markTab === 'upload') {
              <div
                class="mark-upload-pane"
                (drop)="onMarkUploadDrop($event)"
                (dragover)="onMarkUploadDragOver($event)"
              >
                @if (markUploadDataUrl) {
                  <img [src]="markUploadDataUrl" alt="Uploaded mark preview" class="upload-preview" />
                } @else {
                  <p>Drop signature files here...</p>
                }

                <label class="upload-browse">
                  Browse
                  <input type="file" accept=".png,.jpg,.jpeg,.bmp" (change)="onMarkUploadInput($event)" />
                </label>
                <small>Supported formats: PNG, JPG, BMP. Max: 2 MB</small>
                @if (markUploadError) {
                  <p class="upload-error">{{ markUploadError }}</p>
                }
              </div>
            }

            <p class="mark-legal">I understand that this is a legal representation of my {{ markKind || 'signature' }}</p>

            <footer class="modal-footer mark-footer">
              <button type="button" class="ghost" (click)="reuseSavedMark()" [disabled]="!canReuseCurrentMark">Use saved</button>
              <button type="button" class="ghost" (click)="closeMarkModal()">Cancel</button>
              <button type="button" class="primary" (click)="saveMarkAndUse()" [disabled]="!canSaveMark">Save & Use</button>
            </footer>
          </section>
        </div>
      }

      @if (isImageModalOpen) {
        <div class="modal-overlay" (click)="closeImageModal()">
          <section class="image-modal" role="dialog" aria-modal="true" aria-label="Image field" (click)="$event.stopPropagation()">
            <header class="modal-header">
              <h2>Image</h2>
              <button type="button" class="modal-close" aria-label="Close image modal" (click)="closeImageModal()">x</button>
            </header>

            <div class="image-body">
              <p class="image-request">Requested image: {{ imageRequestedLabel }}</p>

              <div class="image-toolbar">
                <label class="upload-browse image-upload-btn">
                  Upload
                  <input type="file" accept=".jpg,.jpeg,.png,.bmp,.svg" (change)="onImageUploadInput($event)" />
                </label>
                <button type="button" class="ghost icon-btn" title="Move">↕</button>
                <button type="button" class="remove-link" (click)="removeImageSelection()">Remove</button>
              </div>

              <div
                #imageStage
                class="image-stage"
                (drop)="onImageUploadDrop($event)"
                (dragover)="onImageUploadDragOver($event)"
                (pointerdown)="onImageStagePointerDown($event)"
                (pointermove)="onImageStagePointerMove($event)"
                (pointerup)="onImageStagePointerUp($event)"
                (pointerleave)="onImageStagePointerUp($event)"
              >
                @if (imageSourceDataUrl) {
                  <img
                    [src]="imageSourceDataUrl"
                    alt="Image preview"
                    class="image-stage-preview"
                    [style.transform]="imagePreviewTransform"
                  />
                } @else {
                  <p class="image-empty">Drop image here or use Upload</p>
                }
              </div>

              <div class="image-zoom-row">
                <button type="button" class="ghost icon-btn" (click)="increaseImageScale()">+</button>
                <button type="button" class="ghost icon-btn" (click)="resetImageTransform()">↺</button>
                <span>ZOOM IMAGE</span>
                <input type="range" min="0.3" max="3" step="0.05" [value]="imageScale" (input)="onImageScaleChange($event)" />
              </div>

              @if (imageModalError) {
                <p class="upload-error">{{ imageModalError }}</p>
              }
            </div>

            <footer class="image-footer">
              <div class="image-help">
                <small>Max file size: 25MB.</small>
                <small>Supported files: JPG, JPEG, SVG, PNG, BMP</small>
              </div>
              <button type="button" class="primary" (click)="saveImageAndUse()" [disabled]="!canSaveImage">Done</button>
            </footer>
          </section>
        </div>
      }

      @if (isDeclineModalOpen) {
        <div class="modal-overlay" (click)="closeDeclineModal()">
          <section class="decline-modal" role="dialog" aria-modal="true" aria-label="Decline signing request" (click)="$event.stopPropagation()">
            <header class="modal-header">
              <h2>Decline signing request</h2>
              <button type="button" class="modal-close" aria-label="Close decline dialog" (click)="closeDeclineModal()">x</button>
            </header>

            <div class="modal-body decline-modal-body">
              <p class="decline-copy">
                Declining is final. You will not be able to sign this document after confirming this decision.
              </p>

              <label class="decline-reason-field">
                <span>Reason (optional)</span>
                <textarea
                  rows="4"
                  [(ngModel)]="declineReason"
                  [disabled]="declining"
                  placeholder="Share why you're declining this request"
                ></textarea>
              </label>
            </div>

            <footer class="modal-footer decline-footer">
              <button type="button" class="ghost" (click)="closeDeclineModal()" [disabled]="declining">Cancel</button>
              <button type="button" class="danger" (click)="decline()" [disabled]="declining">
                {{ declining ? 'Declining...' : 'Confirm decline' }}
              </button>
            </footer>
          </section>
        </div>
      }

      @if (isDisclosureModalOpen) {
        <div class="modal-overlay" (click)="closeDisclosureModal()">
          <section class="disclosure-modal" role="dialog" aria-modal="true" aria-label="Electronic Signature Disclosure" (click)="$event.stopPropagation()">
            <header class="modal-header">
              <h2>Electronic Signature Disclosure</h2>
              <button type="button" class="modal-close" aria-label="Close disclosure" (click)="closeDisclosureModal()">x</button>
            </header>

            <div class="modal-body">
              @for (section of disclosureTermsSections; track $index) {
                <p>
                  @if (section.title) {
                    <strong>{{ section.title }} - </strong>
                  }
                  {{ section.text }}
                </p>
              }
            </div>

            <footer class="modal-footer">
              <button type="button" class="primary modal-ok" (click)="closeDisclosureModal()">OK</button>
            </footer>
          </section>
        </div>
      }
    }
  </div>
</section>
